import EchoService from "@/core/services/EchoService";
import {
    DetectionCaptureIpcResult,
    DetectionCaptureOptions,
    DetectionCaptureSuccess,
    RemoteCommand,
    RemoteCommandEvent,
    RemoteCommandResultPayload
} from "@/core/interfaces/RemoteAssistance";
import {calibratorForLgo} from "@/cardless/calibration";
import useCpsLocator from "@/amelipro/composables/useCpsLocator";
import {supportsCpsDetection} from "@/core/services/shellCapabilities";

const REMOTE_COMMAND_EVENT = '.remote-assistance.requested';
const REMOTE_RESULT_WHISPER = 'remote-assistance.result';

/**
 * Listen for remote assistance commands sent from the Apodis Admin (diagnostics,
 * troubleshooting) and answer them with the result of their execution.
 */
const useRemoteAssistance = () => {
    let channelName: string | null = null;
    let channel: {whisper: (event: string, data: RemoteCommandResultPayload) => unknown} | null = null;

    /**
     * Convert captured image bytes to base64 for the JSON payload.
     */
    const toBase64 = (bytes: Uint8Array): string => {
        const chunkSize = 0x8000;
        let binary = '';

        for (let index = 0; index < bytes.length; index += chunkSize) {
            binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
        }

        return btoa(binary);
    };

    /**
     * Capture the LGO window through the main process.
     * Fails when the installed Electron does not expose `detection:capture` yet,
     * or when the pharmacy is not in cardless detection mode.
     */
    const capture = async (options: DetectionCaptureOptions): Promise<DetectionCaptureSuccess> => {
        const result = await window.ipcRenderer.invoke('detection:capture', options) as DetectionCaptureIpcResult | null;

        if (!result) {
            throw new Error('Capture failed');
        }

        if ('error' in result) {
            throw new Error(result.error);
        }

        return result;
    };

    /**
     * Return what the detection sees: the capture, the OCR text and the crop config.
     */
    const detectionCapture = async (): Promise<Record<string, unknown>> => {
        const result = await capture({formats: ['png'], parse: true});

        return {
            image: toBase64(new Uint8Array(result.png ?? [])),
            patient: result.patient,
            config: result.config,
            meta: result.meta,
        };
    };

    /**
     * Run the web-side autocalibration on a fresh capture. The computed crop
     * config is persisted only when `params.save` is true (dry-run by default).
     */
    const detectionCalibrate = async (params?: Record<string, unknown>): Promise<Record<string, unknown>> => {
        const result = await capture({formats: ['png', 'gray'], parse: false});

        if (!result.gray) {
            throw new Error('Grayscale rendition unavailable (outdated Electron version)');
        }

        const calibrator = calibratorForLgo(result.meta.lgoId);

        if (!calibrator) {
            throw new Error(`No calibrator available for LGO ${result.meta.lgoId}`);
        }

        const calibration = calibrator.calibrate(
            {
                data: new Uint8Array(result.gray.data),
                width: result.gray.width,
                height: result.gray.height,
            },
            result.meta.screen.scaleFactor,
        );

        const save = params?.save === true;

        if (save) {
            await window.ipcRenderer.invoke('cardless-detection-set-crop-config', JSON.stringify(calibration.config));
        }

        return {
            config: calibration.config,
            debug: calibration.debug,
            saved: save,
            image: toBase64(new Uint8Array(result.png ?? [])),
            meta: result.meta,
        };
    };

    /**
     * Check the Sesam Vitale software and reader compatibility.
     */
    const detectionCheckReader = async (): Promise<Record<string, unknown>> => {
        const check = await window.ipcRenderer.invoke('detection:check-reader') as Record<string, unknown> | null;

        if (!check) {
            throw new Error('Reader check failed');
        }

        if (typeof check.error === 'string') {
            throw new Error(check.error);
        }

        return check;
    };

    /**
     * Report what this workstation can tell about the card in its reader.
     *
     * The answer says which source it came from: the card middleware, which
     * speaks for every card family and carries the holder, the entity and the
     * validity — or the Vitale reading API it falls back on, which says nothing
     * at all about a family it turns down. On a real pharmacy workstation,
     * that is the first thing worth knowing from afar.
     */
    const cpsIdentity = async (): Promise<Record<string, unknown>> => {
        const detection = await useCpsLocator().probe({fresh: true});

        return {
            card: detection,
            supports: {
                detection: supportsCpsDetection(),
            },
        };
    };

    /**
     * The commands this Connect version can execute.
     */
    const commands: Record<string, (params?: Record<string, unknown>) => Promise<Record<string, unknown>>> = {
        [RemoteCommand.DETECTION_CAPTURE]: detectionCapture,
        [RemoteCommand.DETECTION_CALIBRATE]: detectionCalibrate,
        [RemoteCommand.DETECTION_CHECK_READER]: detectionCheckReader,
        [RemoteCommand.CPS_IDENTITY]: cpsIdentity,
    };

    /**
     * Execute a remote command and whisper its result back on the channel.
     * The Admin listens with `listenForWhisper`, so no server-side endpoint is
     * involved — Reverb's `max_message_size` must allow image payloads
     * (its default is 10 kB).
     */
    const handleCommand = async (request: RemoteCommandEvent, hostName: string) => {
        const payload: RemoteCommandResultPayload = {
            id: request.id,
            hostname: hostName,
            command: request.command,
            status: 'success',
        };

        try {
            const command = commands[request.command];

            if (!command) {
                throw new Error(`Unknown command: ${request.command}`);
            }

            payload.data = await command(request.params);
        } catch (error) {
            payload.status = 'error';
            payload.error = error instanceof Error ? error.message : 'Command failed';
        }

        channel?.whisper(REMOTE_RESULT_WHISPER, payload);
    };

    /**
     * Subscribe to the pharmacy remote assistance channel.
     *
     * @param organizationId The organization of the pharmacy.
     * @param hostName The host name of this device, used to filter targeted commands.
     */
    const subscribe = (organizationId: number | undefined, hostName: string | null) => {
        const echo = EchoService.connect();

        if (!echo || !organizationId || !hostName) {
            return;
        }

        channelName = `connect.remote-assistance.${organizationId}`;
        channel = echo.private(channelName);
        echo.private(channelName).listen(REMOTE_COMMAND_EVENT, (request: RemoteCommandEvent) => {
            if (request.hostname && request.hostname !== hostName) {
                return;
            }

            handleCommand(request, hostName);
        });
    };

    /**
     * Leave the pharmacy remote assistance channel.
     */
    const unsubscribe = () => {
        if (channelName) {
            EchoService.leave(channelName);
            channelName = null;
            channel = null;
        }
    };

    return {
        subscribe,
        unsubscribe
    };
};

export default useRemoteAssistance;
